import {
  mergeTagsService,
  type MergeTagCategory,
  type Surface,
} from "@/services/mergeTags";

export interface ResolveMergeTagsOptions {
  /** Editing surface; drives the base set via `SURFACE_AVAILABILITY`. */
  surface?: Surface;
  /** If set, restrict to exactly these identifiers (within the surface set). */
  allowedTags?: string[];
  /** Identifiers to exclude from the surface set. */
  hiddenTags?: string[];
  /**
   * Extra categories appended after the surface catalog (e.g. connector
   * loyalty metadata tags). Already curated by the host — not re-filtered by
   * surface availability.
   */
  extraCategories?: MergeTagCategory[];
}

export interface ResolvedMergeTags {
  /** Catalog categories the editor should offer (picker + typeahead). */
  categories: MergeTagCategory[];
  /**
   * Identifiers the editor is allowed to recognize/chip. `undefined` means
   * "no restriction" (chip any recognized tag) — used when the input is
   * unscoped and carries no allow/deny list.
   */
  allowedIdentifiers: Set<string> | undefined;
}

/**
 * Resolves the merge tags an {@link InlineLexicalInput} should offer and detect.
 *
 * The catalog decides *where a tag's data is available* (`availability`, mapped
 * to a `surface` via `SURFACE_AVAILABILITY`). The component decides *what to
 * actually show* on top of that, via an optional allow/deny list — e.g. a
 * subject field on the `subjectPreheader` surface that exposes only
 * `customer.first_name`.
 *
 * One resolved result feeds the picker, the typeahead, and auto-detect so they
 * can never drift apart.
 */
export function resolveMergeTags({
  surface,
  allowedTags,
  hiddenTags,
  extraCategories,
}: ResolveMergeTagsOptions): ResolvedMergeTags {
  const allow = allowedTags ? new Set(allowedTags) : undefined;
  const hide = hiddenTags ? new Set(hiddenTags) : undefined;

  const filterTags = (category: MergeTagCategory): MergeTagCategory => ({
    ...category,
    tags: category.tags.filter(
      (tag) =>
        (!allow || allow.has(tag.identifier)) &&
        (!hide || !hide.has(tag.identifier)),
    ),
  });

  const categories = [
    ...mergeTagsService.list({ surface }).map(filterTags),
    ...(extraCategories ?? []).map(filterTags),
  ].filter((category) => category.tags.length > 0);

  // Gate detection only when the input is actually scoped — an unscoped input
  // with no allow/deny list keeps the permissive "chip anything" behavior.
  const restricted =
    surface !== undefined || allow !== undefined || hide !== undefined;
  const allowedIdentifiers = restricted
    ? new Set(
        [
          ...categories.flatMap((category) =>
            category.tags.map((tag) => tag.identifier),
          ),
          // Explicitly-allowed identifiers are detectable even when omitted from
          // the picker — e.g. hidden legacy aliases (cart_url) in existing drafts.
          ...(allow ?? []),
        ].filter((id) => !hide?.has(id)),
      )
    : undefined;

  return { categories, allowedIdentifiers };
}
